import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { adminApi, apiError, fmtDate } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { useProduct, eur } from "@/lib/product";

const FIELDS = [
  ["normal_price", "Prix public conseillé (prix barré) TTC (€)", "number"],
  ["promo_price", "Prix promo TTC (€)", "number"],
  ["shipping_fee", "Frais de livraison par commande (€)", "number"],
  ["contact_email", "Email affiché aux clients", "email"],
  ["seller_email", "Email(s) de réception des commandes (séparés par des virgules)", "email"],
  ["admin_recovery_email", "E-mail de récupération admin (mot de passe oublié)", "email"],
];

export const SettingsTab = () => {
  const { product, reload } = useProduct();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [promos, setPromos] = useState([]);
  const [running, setRunning] = useState(false);
  const [sendingDigest, setSendingDigest] = useState("");

  useEffect(() => {
    adminApi.get("/settings").then(({ data }) => setF(data)).catch((e) => toast.error(apiError(e)));
    adminApi.get("/promos").then(({ data }) => setPromos(data)).catch(() => {});
  }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await adminApi.post("/reminders/run");
      toast.success(`Relances : #1 ×${data.relance_1}, #2 ×${data.relance_2} · abandonnées ${data.abandonnees}${data.relances_activees ? "" : " (relances désactivées)"}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRunning(false);
    }
  };

  const sendDigest = async (path = "/digest/send") => {
    setSendingDigest(path);
    try {
      const { data } = await adminApi.post(path);
      const ok = data.filter((r) => r.ok).length;
      ok === data.length ? toast.success(`Bilan envoyé à ${data.map((r) => r.to).join(", ")}`) : toast.error(`Bilan : ${ok}/${data.length} envoi(s) réussi(s)`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSendingDigest(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await adminApi.put("/settings", {
        normal_price: Number(f.normal_price), promo_price: Number(f.promo_price),
        shipping_fee: Number(f.shipping_fee), promo_end_local: f.promo_end_local,
        contact_email: f.contact_email, seller_email: f.seller_email,
        reminders_enabled: f.reminders_enabled, reminder_promo_code: f.reminder_promo_code || null, klarna_enabled: f.klarna_enabled,
        digest_enabled: f.digest_enabled, digest_time: f.digest_time, admin_recovery_email: f.admin_recovery_email,
        weekly_enabled: f.weekly_enabled,
      });
      setF(data);
      await reload();
      toast.success("Réglages enregistrés — le site est à jour.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!f) return <Loader2 className="w-6 h-6 animate-spin text-sky-600" />;

  return (
    <form onSubmit={save} data-testid="settings-form" className="order-card p-6 sm:p-8 max-w-xl flex flex-col gap-5">
      <p data-testid="settings-payment-mode" className={`self-start rounded-full px-3 py-1.5 text-xs font-bold ${f.payment_mode.startsWith("RÉEL") ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
        Mode de paiement : {f.payment_mode}
      </p>
      {FIELDS.map(([k, label, type]) => (
        <label key={k} className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-slate-800">{label}</span>
          <input type={type === "email" ? "text" : type} {...(type === "number" ? { step: "0.01", min: "0" } : { inputMode: "email" })} data-testid={`settings-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="field-input" required />
        </label>
      ))}
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-semibold text-slate-800">Fin de l'offre (heure de Paris)</span>
        <input type="datetime-local" data-testid="settings-promo_end-input" value={f.promo_end_local} onChange={(e) => setF({ ...f, promo_end_local: e.target.value })} className="field-input" required />
      </label>
      <div data-testid="settings-digest-block" className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4">
        <label className="flex items-center gap-3 text-sm font-semibold text-slate-800 cursor-pointer">
          <Checkbox data-testid="settings-digest-enabled-checkbox" checked={f.digest_enabled} onCheckedChange={(v) => setF({ ...f, digest_enabled: v === true })} />
          Bilan du soir activé
        </label>
        <label className="flex items-center gap-3 text-sm text-slate-700">
          Heure d'envoi (Paris)
          <input type="time" data-testid="settings-digest-time-input" value={f.digest_time} onChange={(e) => setF({ ...f, digest_time: e.target.value })} className="field-input !w-32" />
        </label>
        <button type="button" onClick={() => sendDigest()} disabled={!!sendingDigest} data-testid="settings-digest-send-now-button" className="btn-ghost-ice !py-2 self-start text-sm">
          {sendingDigest === "/digest/send" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer le bilan maintenant"}
        </button>
        <p className="text-xs text-slate-500">Envoyé chaque jour aux e-mails de réception des commandes. L'envoi manuel (test) ne compte pas comme le bilan du jour.</p>
      </div>
      <div data-testid="settings-weekly-block" className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4">
        <label className="flex items-center gap-3 text-sm font-semibold text-slate-800 cursor-pointer">
          <Checkbox data-testid="settings-weekly-enabled-checkbox" checked={f.weekly_enabled} onCheckedChange={(v) => setF({ ...f, weekly_enabled: v === true })} />
          Bilan hebdomadaire activé
        </label>
        <button type="button" onClick={() => sendDigest("/digest/weekly/send")} disabled={!!sendingDigest} data-testid="settings-weekly-send-now-button" className="btn-ghost-ice !py-2 self-start text-sm">
          {sendingDigest === "/digest/weekly/send" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer le bilan hebdo maintenant"}
        </button>
        <p className="text-xs text-slate-500">Chaque lundi à 08:00 (Paris) : semaine précédente, du lundi au dimanche, comparée à la semaine d'avant.</p>
      </div>
      <div data-testid="settings-webhook-status" className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 text-sm">
        <span className="font-semibold text-slate-800">Webhook Stripe</span>
        <span data-testid="settings-webhook-badge" className={`rounded-full px-2.5 py-1 text-xs font-bold ${f.webhook_configured ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
          {f.webhook_mode}
        </span>
        <span data-testid="settings-webhook-last" className="text-xs text-slate-500">
          Dernier webhook reçu : {f.webhook_last_received_at ? fmtDate(f.webhook_last_received_at) : "aucun"}
        </span>
      </div>
      <label className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 text-sm font-semibold text-slate-800 cursor-pointer">
        <Checkbox data-testid="settings-klarna-enabled-checkbox" checked={f.klarna_enabled} onCheckedChange={(v) => setF({ ...f, klarna_enabled: v === true })} />
        Afficher le paiement Klarna 3x/4x
      </label>
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4">
        <label className="flex items-center gap-3 text-sm font-semibold text-slate-800 cursor-pointer">
          <Checkbox data-testid="settings-reminders-enabled-checkbox" checked={f.reminders_enabled} onCheckedChange={(v) => setF({ ...f, reminders_enabled: v === true })} />
          Relances panier activées
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-slate-800">Code promo à inclure dans la relance #2</span>
          <select data-testid="settings-reminder-promo-select" value={f.reminder_promo_code || ""} onChange={(e) => setF({ ...f, reminder_promo_code: e.target.value })} className="field-input">
            <option value="">Aucun</option>
            {promos.map((p) => <option key={p.code} value={p.code}>{p.code}{p.active ? "" : " (inactif)"}</option>)}
          </select>
        </label>
        <button type="button" onClick={runNow} disabled={running} data-testid="settings-run-reminders-button" className="btn-ghost-ice !py-2.5 self-start">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : "Lancer les relances maintenant"}
        </button>
      </div>
      {product && (
        <p data-testid="settings-live-price" className="text-sm text-slate-600">
          Prix actuellement affiché sur le site : <strong>{eur(product.current_price)}</strong> {product.promo_active ? "(offre active)" : "(prix public conseillé)"} + livraison {eur(product.shipping_fee)}
        </p>
      )}
      <button type="submit" disabled={saving} data-testid="settings-save-button" className="btn-heat self-start disabled:opacity-70">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enregistrer"}
      </button>
    </form>
  );
};
